/**
 * ForYouSection - the "For you" block above an event's feed (Track 2
 * Phase A, spec 3.1/3.4/4): the posts THIS viewer can help with at the event,
 * served by `useEventHelpable` (the matcher's already-judged matches - no new
 * matching). With zero matches it renders the event match-gate card, not absence
 * (P8): the founder's Figma gate (fb6 r2), profile gate or post invitation.
 * The matches state ports the approved mock (`2026-07-12-for-you-mock-v4-approved.html`):
 * collapse shell > clip > section [gradient layer + header + card rows +
 * "+N more" facepile footer]. The zero state is the ForYouGateCard - the matches
 * header over a frosted tease (profile gate) or the plain framed content (post
 * invitation), but none of the matches state's live wash/cascade - inside the
 * same collapse shell + inner padding layer.
 *
 * IDENTIFIER CONSISTENCY (binding, W1 review note): `eventId` is passed to the
 * hook UNCHANGED, and every Phase A surface (this section, the LivePill chip,
 * the feed chips) MUST use the SAME identifier form for a given event - slug or
 * uuid, never mixed - so they share one `eventHelpableKey(eventId)` cache entry
 * and one fetch (spec P5).
 *
 * State machine (spec 3.4):
 *   - flag off               -> null (nothing mounts; the hook never fetches)
 *   - loading                -> collapsed shell (zero space, no skeleton - P6)
 *   - server `enabled:false` -> no content (kill = absence; the hook collapses
 *     kill and fetch-error to the same `{ enabled: false }` result, and both
 *     render as the closed shell - silent, retried on the next focus refetch)
 *   - >=1 match              -> header + up to forYouMaxCards cards + footer
 *   - 0 matches              -> the event match-gate card (profile gate or post invitation, fb6)
 *
 * THE LOAD MOMENT (W3, spec 3.3 - the approved mock's CSS is the contract,
 * ported not reinvented): on data-resolve the body first COMMITS in its boot
 * pose inside the still-closed shell (the boot-race rule from spec 3.1 - the
 * collapsed/gradient-less state is never applied post-mount), then releases:
 * expand (grid-rows 0fr->1fr, `forYouExpandMs`, ease-out-strong) -> gradient
 * wash (clip-path circle from the icon corner + saturation bloom,
 * `forYouWashMs`, starting `WASH_DELAY_MS` in) -> one border-light revolution
 * (accent, registered `--fy-angle`) -> rows cascade (`forYouStaggerMs` apart).
 * One-time per mount AND per session (sessionStorage per event+surface - the
 * live pill's enter rule); session replays and `prefers-reduced-motion` render
 * the settled state instantly. New matches on refetch spring-insert in place +
 * fire a one-shot perimeter ripple; the ✕ plays scale-fade + grid heal BEFORE
 * the dismissal data flow runs (deferred unmount via `useOwnedExit`).
 */
import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { ChevronDown, ChevronUp, Pencil, X } from 'lucide-react';
import { MATCH_CARD_TITLE, MatchAvatarRing, MatchCardMark } from '@components/matching/MatchCard';
import { NudgeCardContent } from '@components/matching/NudgeCardContent';
import {
  NUDGE_GLYPH_CLASS,
  PROFILE_EMPTY_NUDGE_CTA,
  PROFILE_EMPTY_NUDGE_HEADLINE,
  profileNudgeIcon,
} from '@components/matching/nudgePresentation';
import { GradientBorderBox } from '@components/ui';
import { AnimatedCollapse, EXPAND_ANIMATION_MS } from '@components/ui/AnimatedCollapse';
import { Avatar, AvatarFallback, AvatarImage } from '@components/ui/avatar';
import { ImgWithFallback } from '@components/ui/img-with-fallback';
import { LIVE_EVENTS_CONFIG } from '@config/liveEvents';
import { useAmplitudeTracking } from '@hooks/useAmplitudeTracking';
import { useCurrentUser } from '@hooks/useCurrentUser';
import { useDismissHelpable } from '@hooks/useDismissHelpable';
import { useEventHelpable } from '@hooks/useEventHelpable';
import { useHelpableAtEventEnabled } from '@hooks/useHelpableAtEventEnabled';
import { useHelpableSurfaceImpression } from '@hooks/useHelpableSurfaceImpression';
import { usePrefersReducedMotion } from '@hooks/usePrefersReducedMotion';
import { useProfileEditCta } from '@hooks/useProfileEditCta';
import { useIncrementalArrivals, useRevealPhase } from '@hooks/useRevealChoreography';
import type { RevealPhase } from '@hooks/useRevealChoreography';
import { useScrollIntoViewOnReveal } from '@hooks/useScrollIntoViewOnReveal';
import { ANALYTICS_EVENTS } from '@lib/analytics-events';
import { buildHelpableEventContext, type HelpableEventContext } from '@lib/analytics/payloads';
import { eventHelpableKey } from '@lib/cache/events';
import { cn } from '@lib/utils';
import type { EventHelpableResponse, EventRead, HelpableAtEventItem } from '@models/api';
import { routes } from '@routes';
import { showEventPostEntry, type EventComposerSurface } from '@utils/eventPostGate';
import { isProfileIncomplete } from '@utils/profileCompletion';
import { removeHelpableItem, restoreHelpableItem } from './helpableSectionCache';
import {
  DISMISS_BUTTON_CLASSES,
  FOCUS_RING_CLASSES,
  useEnterSettle,
  useOwnedExit,
} from './liveMotion';

/** Overflow faces shown in the "+N more" footer pile (mock v4 shows two). */
const MORE_FACES_MAX = 2;

/**
 * sessionStorage namespace for the load-moment gate - owned by ForYouSection,
 * keyed per event (`for-you-entered:<eventId>`). Mirrors the live pill's
 * enter rule: one prefix per surface, the gate evaluated per mount.
 */
const ENTER_KEY_PREFIX = 'for-you-entered:';

/** The event page's sticky tab strip (marked in EventTabs) - the occlusion the "For you" card's
 * expand scroll must clear so its header lands just below it, not underneath (fb5 item 6). */
const STICKY_TABS_SELECTOR = '[data-event-tabs-sticky]';
/** Gap left between the sticky strip and the card header after the expand scroll. */
const EXPAND_SCROLL_STICKY_GAP = 8;
/** Top inset used when the sticky strip is absent (non-event hosts / tests); matches the hook's
 * own default so behavior is unchanged off the event page. */
const EXPAND_SCROLL_FALLBACK_OFFSET = 16;

/*
 * Fixed choreography offsets of the approved mock (v4) - the SHAPE of the
 * load moment, ported verbatim. The tunable durations (expand, wash, stagger)
 * are levers in LIVE_EVENTS_CONFIG and are always read from there.
 */
/** The wash starts this far into the expand (mock: `.fy-grad` delay .28s). */
const WASH_DELAY_MS = 280;
/** First row (and the invitation) releases here (mock releaseBoot: 380ms). */
const CASCADE_BASE_DELAY_MS = 380;
/** The border light starts here... (mock `.wow::before` delay .6s). */
const BORDER_LIGHT_DELAY_MS = 600;
/** ...and runs one revolution (mock fyTravel: 1.05s). */
const BORDER_LIGHT_MS = 1050;
/** Row spring transform duration (mock `.fy-row` transition: .3s). */
const ROW_ENTER_MS = 300;
/**
 * Row exit opacity duration (mock closing: opacity .16s) - the unmount cue:
 * the mock removes the row on its FIRST transitionend, which is this fade
 * (the grid heal's tail plays out on the remaining rows' shift).
 */
const ROW_EXIT_MS = 160;

/**
 * When the load moment has fully landed (its last-finishing track): the wow
 * accents and the cascade delays drop here. Derived instead of the mock's
 * flat 2s cleanup so retuning the config levers keeps the settle honest.
 */
const SETTLE_AFTER_MS = Math.max(
  LIVE_EVENTS_CONFIG.forYouExpandMs,
  WASH_DELAY_MS + LIVE_EVENTS_CONFIG.forYouWashMs,
  BORDER_LIGHT_DELAY_MS + BORDER_LIGHT_MS,
  CASCADE_BASE_DELAY_MS +
    (LIVE_EVENTS_CONFIG.forYouMaxCards - 1) * LIVE_EVENTS_CONFIG.forYouStaggerMs +
    ROW_ENTER_MS,
);

/**
 * The wash durations are config levers, fed to the CSS (`for-you-wash-motion`
 * in tailwind.css) as custom properties - the utility hardcodes no lever.
 */
const WASH_MOTION_VARS = {
  '--fy-wash-ms': `${LIVE_EVENTS_CONFIG.forYouWashMs}ms`,
  '--fy-wash-delay': `${WASH_DELAY_MS}ms`,
} as CSSProperties;

/** Expand duration lever (spec 3.3) applied to the collapse shell. */
const EXPAND_DURATION_STYLE: CSSProperties = {
  transitionDuration: `${LIVE_EVENTS_CONFIG.forYouExpandMs}ms`,
};

/**
 * The post-invitation card copy (fb6 ruling 3): shown at zero matches to a viewer whose profile is
 * complete (or when no profile path can be built - ruling 7). Assembled from the locked brand
 * language and reviewed against the tone-of-voice skill; exported so the tests pin these constants
 * rather than re-typing the strings. The support line is byte-identical to the previously locked
 * invitation line (capital T, final period).
 */
export const FOR_YOU_INVITATION_HEADLINE = 'find the ones who care';
export const FOR_YOU_INVITATION_COPY = "The people you're looking for may already be here.";
export const FOR_YOU_INVITATION_CTA = 'post what you need';

/**
 * The profile-gate support line on the EVENT surface (fb6 r2 - the founder's "what will this achieve"
 * fix). The question-detail profile_empty nudge keeps its generic PROFILE_EMPTY_NUDGE_COPY; here we
 * name what completing the profile unlocks AT THIS EVENT, so the gate reads as the door to the event's
 * matching. Headline + CTA stay the shared PROFILE_EMPTY_* constants (one gate identity) - only the
 * support line is event-contextual. Lowercase meetball tone, no em dashes; exported so the test pins
 * the constant rather than a re-typed literal.
 */
export const FOR_YOU_PROFILE_GATE_COPY =
  'complete your profile to connect with the right people at this event.';

export interface ForYouSectionProps {
  /**
   * The event's identifier, forwarded to `useEventHelpable` unchanged.
   * Use the SAME form (slug or uuid) as every other Phase A helpable surface
   * for this event - see the identifier-consistency note in the file header.
   */
  eventId: string;
  /**
   * The event, used ONLY to enrich this section's analytics with the
   * standard event + parent-space context (W6). `eventId` stays the cache/fetch
   * identifier (the identifier-consistency contract above); `event` is the
   * context object every live_surface_* payload wants. Optional so the state /
   * motion suites can mount the section with an id alone (analytics no-ops under
   * test); the real caller (EventForYouSlot) always has the event and passes it,
   * so production events carry name/slug/space. When absent, analytics falls
   * back to `event_id` from `eventId` (name/slug/space null).
   */
  event?: EventRead;
  /**
   * The Event page's create-post entry (useEventComposerEntry's `openComposer`), which the post
   * card calls with its own surface. The page owns it, and the attend prompt it may open, because
   * the card does not outlive a Going: once the member is enrolled the zero state can give way to
   * matches. Absent off the Event page and for an Event with no host Space; the post card then
   * opens the plain composer. Pass a function that keeps its identity across the caller's
   * renders: a new one re-renders the whole section.
   */
  onOpenComposer?: (surface: EventComposerSurface) => void;
}

/**
 * Viewer gate as ONE hook call (`useHelpableAtEventEnabled`), called
 * unconditionally before the early return so the LiveAvatarRing/LiveStateChip
 * split still holds: the gated child (`FlaggedForYouSection`) owns every OTHER
 * hook and mounts/unmounts as a unit when the gate flips. The gate is the flag
 * OR the viewer being a Meetball admin (always-on dogfood, flag governs only the
 * public flip), so admins see the section with the flag off.
 */
export function ForYouSection({ eventId, event, onOpenComposer }: ForYouSectionProps) {
  const enabled = useHelpableAtEventEnabled();
  if (!enabled) {
    return null;
  }
  return <FlaggedForYouSection eventId={eventId} event={event} onOpenComposer={onOpenComposer} />;
}

/**
 * The collapse shell (mock `.fy-collapse` > `.fy-clip`): the house
 * grid-template-rows 0fr/1fr trick, so the section occupies ZERO space until
 * its answer arrives (P6 - no skeleton, a slow fetch never shows a hole).
 * Loading, fetch-error, and server-kill all keep it closed with an empty clip.
 * The shell opens one frame AFTER the body committed in its boot pose (`open`
 * keys off the phase, never off `resolved` directly - the boot-race rule).
 * Session-seen and reduced-motion mounts start at `settled`, so they open the
 * moment the answer arrives: the expand still plays for them (opening is a
 * state change, not wow dressing), while reduced motion drops it in CSS.
 *
 * The clip's `overflow-hidden` (which the 0fr collapse needs) would otherwise
 * shear the card's `shadow-soft` and the `inset:-1px` border-light/ripple
 * accents at its bounds, and the section would hug the sticky tab strip's
 * fade-below-sticky overlay. So the resolved body rides an inner padding layer
 * INSIDE the clip: `pt-4` clears the fade (matching the feed's `py-4` first-card
 * rhythm), `px-2`/`pb-2` give the shadow + accents room. This padding sits
 * inside the clipped region, so it collapses to nothing with the shell (P6 - no
 * height when closed/killed); the `px-2` here + the slot's `px-2` = the feed's
 * `px-4` card width.
 */
function FlaggedForYouSection({ eventId, event, onOpenComposer }: ForYouSectionProps) {
  const { items, total, enabled, isLoading } = useEventHelpable(eventId);
  const resolved = enabled && !isLoading;
  const phase = useRevealPhase(eventId, resolved, {
    keyPrefix: ENTER_KEY_PREFIX,
    settleAfterMs: SETTLE_AFTER_MS,
  });
  const open = resolved && phase !== 'boot';

  // Standard event + parent-space context for every event this section fires
  // (W6, spec 3.5). Memoized so it stays referentially stable as a hook dep and
  // never re-fires the impression on unrelated re-renders.
  const eventContext = useMemo(() => buildHelpableEventContext(event, eventId), [event, eventId]);

  // `for_you_section` displayed: once per mount, the render the section is
  // actually SHOWN (`open`, not merely `resolved`) - a first-time motion-on
  // viewer holds a zero-height boot pose for a frame after resolve, and an
  // impression there would log an unseen surface. Cards OR invitation both count
  // (the invitation is still the section being shown, with `helpable_count: 0`);
  // `helpable_invitation_shown` fires ADDITIONALLY for the 0-match case (the
  // invitation-specific funnel), so the two never collapse.
  useHelpableSurfaceImpression({
    surface: 'for_you_section',
    eventContext,
    helpableCount: total,
    when: open,
    appearanceKey: eventId,
  });

  // The section-scoped dismiss lives HERE, not in ForYouSectionBody, so its mutation observer
  // outlives the body: dismissing the LAST card optimistically writes total 1->0, which unmounts
  // ForYouSectionBody and mounts the zero-state gate. React Query v5 drops a per-call `mutate()`
  // callback when its observing component unmounts before the mutation settles, so a body-owned
  // observer would silently drop the eventHelpableKey rollback + invalidation on a failed last-card
  // DELETE (error toast alongside an apparent-success zero state). FlaggedForYouSection stays mounted
  // across the 1->0 transition (only the flag / admin gate above ever unmounts it), so the per-call
  // callbacks always run (C1).
  const dismissCard = useDismissEventHelpable(eventId);

  return (
    <div
      className={cn(
        'grid transition-[grid-template-rows] ease-out-strong motion-reduce:transition-none',
        open ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
      )}
      style={EXPAND_DURATION_STYLE}
    >
      <div className="min-h-0 overflow-hidden">
        {resolved ? (
          // Inner padding layer (see the docstring): clears the sticky fade and
          // gives the card's shadow + border-light room, all inside the clip so
          // it collapses to zero with the shell (P6).
          <div className="px-2 pt-4 pb-2">
            {total === 0 ? (
              // Zero matches: the event match-gate card (fb6 r2). It wears the
              // matches header so it reads as the matching surface, but keeps none
              // of the matches state's live wash / cascade / border-light (those
              // stay in ForYouSectionBody). Which gate (profile vs post) is the
              // zero-state's own call. The collapse shell above still opens on
              // resolve, and the section + invitation impressions keep their timing.
              <ForYouZeroState
                event={event}
                boot={phase === 'boot'}
                eventContext={eventContext}
                onOpenComposer={onOpenComposer}
              />
            ) : (
              <ForYouSectionBody
                items={items}
                total={total}
                phase={phase}
                eventContext={eventContext}
                dismissCard={dismissCard}
              />
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
}

function ForYouSectionBody({
  items,
  total,
  phase,
  eventContext,
  dismissCard,
}: {
  items: HelpableAtEventItem[];
  total: number;
  phase: RevealPhase;
  /** Prebuilt event + parent-space analytics context (W6). */
  eventContext: HelpableEventContext;
  /** The section-scoped dismiss, hoisted to FlaggedForYouSection so its mutation observer survives
   *  this body's last-card 1->0 unmount (C1). */
  dismissCard: (questionId: string) => Promise<void>;
}) {
  const reducedMotion = usePrefersReducedMotion();
  const { trackEvent } = useAmplitudeTracking();
  const { freshIds, wave } = useIncrementalArrivals(items.map(item => item.question_id));
  const [expanded, setExpanded] = useState(false);
  // Disclosure a11y: a stable id for the reveal container (the aria-controls
  // target, F3), a ref on the card so the reveal can scroll it into view, and a
  // ref on the single persistent toggle so a collapse keeps focus on it when the
  // revealed region turns inert.
  const revealId = useId();
  const sectionRef = useRef<HTMLElement>(null);
  const toggleRef = useRef<HTMLButtonElement>(null);
  const boot = phase === 'boot';
  const visible = items.slice(0, LIVE_EVENTS_CONFIG.forYouMaxCards);
  // The folded rows that ARE available client-side (the server sends at most
  // HELPABLE_AT_EVENT_LIMIT items) - revealed IN PLACE on expand.
  const overflow = items.slice(LIVE_EVENTS_CONFIG.forYouMaxCards);
  // "N more" counts everything the section folded away: the FULL match count
  // (before the server cap) minus the cards actually shown.
  const moreCount = Math.max(0, total - visible.length);
  // Edge guard (F5): the disclosure is only truly expanded while overflow
  // remains. If dismissing the revealed rows empties the overflow (moreCount 0),
  // treat the block as collapsed - a pure derivation, no state reset - so the
  // stale expanded shell never renders as an empty pane.
  const isExpanded = expanded && moreCount > 0;
  // Matches beyond the client-side cap: `overflow` can't cover these, so an
  // honest muted tail names them rather than over-promising a dead-end row.
  const beyondCap = Math.max(0, total - items.length);
  // Carry the question_id (not just the author) so the footer keys on it: one
  // author with two overflow items would otherwise collide on a duplicate React
  // key (author id is not unique across items).
  const moreFaces = items
    .slice(visible.length, visible.length + MORE_FACES_MAX)
    .map(overflowItem => ({ questionId: overflowItem.question_id, author: overflowItem.author }));

  // Final card height = its current box + the revealed overflow, which stays
  // mounted in an overflow-hidden wrapper and so reports its true height via
  // scrollHeight even while visually collapsed. Knowing the end size up front lets
  // the scroll run concurrently with the reveal (mirrors SuggestedHelpersSection).
  const getFinalHeight = useCallback(() => {
    const el = sectionRef.current;
    if (!el) return 0;
    let height = el.getBoundingClientRect().height;
    el.querySelectorAll<HTMLElement>('[data-reveal-content]').forEach(node => {
      height += node.scrollHeight;
    });
    return height;
  }, []);

  // The sticky tab strip occludes the top of the page; measure its stuck bottom at fire time so
  // the reveal scroll lands the card header just below it, not under it - the browser's
  // scroll-anchoring otherwise slides the header up under the tabs as the card grows above the
  // tapped toggle (fb5 item 6). Stable callback (measured fresh each call), so it never re-fires
  // the scroll effect on its own.
  const getStickyOffset = useCallback(() => {
    const strip = document.querySelector<HTMLElement>(STICKY_TABS_SELECTOR);
    if (!strip) {
      return EXPAND_SCROLL_FALLBACK_OFFSET;
    }
    // Its sticky `top` resolves to the header height; its own box is the strip.
    const stickyTop = parseFloat(getComputedStyle(strip).top) || 0;
    return stickyTop + strip.getBoundingClientRect().height + EXPAND_SCROLL_STICKY_GAP;
  }, []);

  // On expand, scroll the whole card into view concurrently with the reveal (shared duration +
  // decelerating easing), landing the header below the sticky tabs; rewinding on collapse. The
  // hook absolute-scrolls each frame, so it wins over scroll-anchoring during the growth. This is a
  // one-shot per toggle (keyed on isExpanded), not a scroll-watching loop.
  useScrollIntoViewOnReveal(sectionRef, isExpanded, {
    durationMs: EXPAND_ANIMATION_MS,
    getFinalHeight,
    getOffsetTop: getStickyOffset,
  });

  // One persistent toggle (mirrors SuggestedHelpersSection): flip the disclosure
  // and, on expand only, record the intent once with the section's standard
  // payload. Analytics fires in the handler, never in the setState updater (which
  // must stay pure - React may double-invoke it).
  const handleToggleExpand = () => {
    const next = !isExpanded;
    setExpanded(next);
    if (next) {
      trackEvent(ANALYTICS_EVENTS.live_for_you_expanded, {
        surface: 'for_you_section',
        helpable_count: total,
        ...eventContext,
      });
    } else {
      // Collapsing makes the revealed region inert; if focus was inside it, keep
      // it on the toggle rather than letting the browser drop it to <body>.
      const active = document.activeElement;
      if (active instanceof HTMLElement && active.closest('[data-reveal-content]')) {
        toggleRef.current?.focus();
      }
    }
  };

  return (
    // <section> with an accessible name has the implicit `region` role (3.6).
    <section
      ref={sectionRef}
      aria-label="Posts you can help with"
      className={cn(
        // The shared matches-card frame look (Figma node 6960:14741): rounded-10,
        // offwhite surface, soft shadow, no border. The wash + header + pill rows sit
        // on top. (ForYou keeps its own header/wash element - not the MatchCard
        // component - since its wash/choreography wiring + `bg-for-you-wash` class
        // are pinned by its suites. Its title + icon reuse MatchCard's actual
        // MATCH_CARD_TITLE / MatchCardMark below, but the row also carries the
        // live match COUNT inline next to the title - MatchCard's header has no
        // slot for that [`trailing` sits at the far edge, past a `flex-1` title
        // block, not tight against it] - so adopting MatchCard's header wholesale
        // would relocate the count rather than being a clean drop-in. Inlined.)
        'relative rounded-[10px] bg-mb-offwhite p-2.5 pb-1.5 shadow-soft',
        // The section itself dims during boot (mock `.foryou.boot` opacity .4).
        'transition-opacity duration-[320ms] ease-out-strong motion-reduce:transition-none',
        boot && 'opacity-40',
      )}
    >
      {/* The gradient lives on its OWN layer so it can APPEAR as the load
          moment: boot holds it as a zero-radius circle at the icon corner,
          desaturated; the release transitions it to the full wash (spec 3.3).
          Durations ride in as config-lever custom properties. The content
          blocks below are `relative z-10` to paint above it. */}
      <div
        aria-hidden="true"
        className={cn(
          'bg-for-you-wash for-you-wash-motion pointer-events-none absolute inset-0 z-0 rounded-[10px]',
          boot
            ? '[clip-path:circle(0px_at_26px_22px)] opacity-25 saturate-[0.25]'
            : '[clip-path:circle(165%_at_26px_22px)] opacity-100 saturate-100',
        )}
        style={WASH_MOTION_VARS}
      />
      {phase === 'wow' ? (
        // One border-light revolution, the load accent (mock `.wow::before`):
        // one-shot by construction - the element exists only during the wow
        // window and leaves with the settle.
        <span aria-hidden="true" className="for-you-border-light" />
      ) : null}
      {wave > 0 && !reducedMotion ? (
        // One-shot perimeter ripple per arrival wave (mock `.ripple::after`);
        // the key remount restarts the animation - React's idiom for the
        // mock's classList re-add + forced-reflow dance.
        <span key={wave} aria-hidden="true" className="for-you-ripple" />
      ) : null}
      <div className="relative z-10 flex items-center gap-1.5 px-1 pt-0.5 pb-2">
        {/* The composed matches-card brand mark: the SAME asset + ~34x28 sizing
            MatchCard's own header gives it (MatchCardMark, reused directly - not
            a lookalike copy), so this card and the question-detail matches card
            wear the identical icon. */}
        <MatchCardMark />
        <span className="text-base font-semibold text-[#1c1c1c]">{MATCH_CARD_TITLE}</span>
        {/* total is always >= 1 here: FlaggedForYouSection routes total === 0 to the zero-state gate,
            so this body only renders with matches - the old `total > 0 ?` guard's null arm was dead (C4). */}
        <span className="text-base font-semibold text-[#1c1c1c] tabular-nums">· {total}</span>
      </div>
      {/* Matches (total > 0) only: header + card rows. The zero-match gate is rendered one
          level up in FlaggedForYouSection (fb6 ruling 5 - the gate is the whole card, with no
          matches header / wash), so this body no longer branches on total. */}
      <div className="relative z-10">
        {visible.map((item, index) => {
          const insert = freshIds.has(item.question_id);
          return (
            <ForYouCard
              key={item.question_id}
              item={item}
              onDismiss={() => dismissCard(item.question_id)}
              boot={boot}
              insert={insert}
              eventContext={eventContext}
              helpableCount={total}
              cascadeDelayMs={
                phase === 'settled' || insert
                  ? null
                  : CASCADE_BASE_DELAY_MS + index * LIVE_EVENTS_CONFIG.forYouStaggerMs
              }
              // Non-last visible rows always keep the trailing gap; the last VISIBLE
              // row keeps it too whenever overflow rows / the toggle follow
              // (moreCount > 0) - only the true tail (no overflow at all) skips it,
              // so it never stacks a phantom gap under the section's own `pb-1.5`
              // (K3 fix, mirrors MatchReveal's `gapAfter`).
              gapAfter={index < visible.length - 1 || moreCount > 0}
            />
          );
        })}
      </div>
      {moreCount > 0 ? (
        <>
          {/* Always-mounted reveal (shared AnimatedCollapse): the overflow rows
                  stay in the DOM so the disclosure animates BOTH ways, and are
                  inert + aria-hidden while collapsed so nothing focusable hides
                  behind the clip. `id` lets the toggle's aria-controls target it. */}
          <AnimatedCollapse open={isExpanded} id={revealId}>
            <div className="relative z-10">
              {overflow.map(overflowItem => (
                <ForYouCard
                  key={overflowItem.question_id}
                  item={overflowItem}
                  onDismiss={() => dismissCard(overflowItem.question_id)}
                  boot={boot}
                  insert={freshIds.has(overflowItem.question_id)}
                  eventContext={eventContext}
                  helpableCount={total}
                  cascadeDelayMs={null}
                  // The toggle button always follows the overflow list, so every
                  // overflow row keeps its trailing gap (unchanged by K3).
                  gapAfter={true}
                />
              ))}
              {beyondCap > 0 ? (
                // The honesty tail (spec P4): matches past the client cap we
                // can't show as rows - muted, non-interactive, no dead-end.
                <p className="text-muted-foreground px-1.5 pt-1 pb-1.5 text-xs">
                  and {beyondCap} more
                </p>
              ) : null}
            </div>
          </AnimatedCollapse>
          {/* One persistent disclosure toggle: its CONTENT flips (collapsed
                  facepile affordance vs "Show less") but the button never unmounts,
                  so focus follows it for free and the collapse animates. */}
          <button
            type="button"
            ref={toggleRef}
            onClick={handleToggleExpand}
            aria-expanded={isExpanded}
            aria-controls={revealId}
            className={cn(
              'text-muted-foreground relative z-10 flex w-full items-center gap-2 rounded-xl px-1.5 pt-1.5 pb-2 text-left text-xs',
              'transition-colors duration-[160ms] ease-out active:bg-black/5',
              FOCUS_RING_CLASSES,
            )}
          >
            {isExpanded ? (
              <>
                Show less
                <ChevronUp aria-hidden="true" className="ml-auto h-4 w-4 shrink-0" />
              </>
            ) : (
              <>
                <ForYouMoreFacepile faces={moreFaces} />
                <span className="tabular-nums">{moreCount} more you can help with</span>
                <ChevronDown aria-hidden="true" className="ml-auto h-4 w-4 shrink-0" />
              </>
            )}
          </button>
        </>
      ) : null}
    </section>
  );
}

/**
 * One helpable row (mock `.fy-rowwrap > .fy-rowclip > .fy-row`): avatar with
 * the soft helpable-green ring, bold author name + the post title, and the
 * judge's reason line (muted, single-line ellipsis). Body and ✕ are sibling
 * buttons (the LiveCompactCard rule) so taps never double-fire.
 *
 * Motion (spec 3.3, all mock-ported): the grid wrapper is the heal/insert
 * ground (grid-rows collapse over 280ms on the swap curve); the row wears the
 * spring transition. Boot cascade: pose down-shifted + transparent, released
 * by the section with a per-row `--fy-row-delay`. Insert: mounts in the
 * preinsert pose (wrapper closed, row up-shifted) and springs open one frame
 * later. Dismiss: `useOwnedExit` plays scale-fade + heal first, then hands the
 * dismissal to the data flow (deferred unmount) - reduced motion dismisses
 * immediately, matching the mock's `reduced` branch.
 */
function ForYouCard({
  item,
  onDismiss,
  boot,
  insert,
  eventContext,
  helpableCount,
  cascadeDelayMs,
  gapAfter,
}: {
  item: HelpableAtEventItem;
  onDismiss: () => void;
  /** True while the section holds its pre-release boot pose. */
  boot: boolean;
  /** True when this card is a NEW match arriving on a refetch (spring insert). */
  insert: boolean;
  /** Prebuilt event + parent-space analytics context (W6). */
  eventContext: HelpableEventContext;
  /** The section's current match total, stamped as `helpable_count` (W6). */
  helpableCount: number;
  /** Cascade delay while the load moment plays; null once settled (an inline
   * delay must not outlive the moment - it would defer later transitions). */
  cascadeDelayMs: number | null;
  /** True unless this is the truly-last rendered pill with nothing but the section's
   * own bottom padding following it (K3 fix): carries the inter-row gap as this row's
   * OWN trailing margin, INSIDE the grid-heal clip below, so a collapsed (0fr) row
   * contributes zero gap (mirrors MatchReveal's MatchFaceRow `gapAfter`) instead of a
   * flat `mb-2` stacking a phantom gap under the section's `pb-1.5` when nothing else
   * follows the last row. */
  gapAfter: boolean;
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const reducedMotion = usePrefersReducedMotion();
  const { trackEvent } = useAmplitudeTracking();
  const { leaving, beginExit, onTransitionEnd } = useOwnedExit(onDismiss, ROW_EXIT_MS);
  const insertSettled = useEnterSettle(() => !insert || reducedMotion);
  const preinsert = !insertSettled;

  // Shared payload for this card's tap / dismiss (W6, spec 3.5): the standard
  // event + parent-space context, the section count, and the item's own
  // question + serve intent (mirrors the suggested-helpers intent fields).
  const cardPayload = {
    surface: 'for_you_card' as const,
    helpable_count: helpableCount,
    question_id: item.question_id,
    surfaced_by_intent: item.surfaced_by_intent,
    ...eventContext,
  };

  const openPost = () => {
    if (leaving) {
      return;
    }
    trackEvent(ANALYTICS_EVENTS.live_surface_tapped, cardPayload);
    navigate(
      routes.question.details(
        { id: item.question_id },
        { returnTo: location.pathname + location.search },
      ),
    );
  };

  const handleDismiss = () => {
    // Guard the exit window (the openPost rule): a double-tap while the row is
    // already leaving must not re-fire live_surface_dismissed - beginExit and
    // the mutation dedupe via firedRef, but the analytics would double-count.
    if (leaving) {
      return;
    }
    // Fire at the moment of intent (before the deferred-exit choreography), so
    // the dismissal is recorded even though the row lingers to animate out.
    // `stage: 2` = the card is gone (the cross-surface dismissal grammar); the
    // For-you card has no fold-to-pill stage 1.
    trackEvent(ANALYTICS_EVENTS.live_surface_dismissed, { ...cardPayload, stage: 2 });
    if (reducedMotion) {
      // Settled-state-instantly rule: no exit transition, dismiss now.
      onDismiss();
      return;
    }
    beginExit();
  };

  const rowPose = boot
    ? 'translate-y-2 scale-[0.98] opacity-0'
    : preinsert
      ? '-translate-y-2 scale-[0.97] opacity-0'
      : leaving
        ? 'translate-y-0 scale-95 opacity-0'
        : 'translate-y-0 scale-100 opacity-100';

  return (
    <div
      className={cn(
        'grid transition-[grid-template-rows] duration-[280ms] ease-swap motion-reduce:transition-none',
        leaving || preinsert ? 'grid-rows-[0fr]' : 'grid-rows-[1fr]',
      )}
    >
      <div className="min-h-0 overflow-hidden">
        <div
          data-leaving={leaving ? '' : undefined}
          onTransitionEnd={onTransitionEnd}
          className={cn(
            // The shared translucent pill row (matches-card treatment). The inter-row gap
            // is this row's OWN trailing `mb-2`, from INSIDE the grid-heal clip, so it
            // collapses cleanly with the row on dismiss/insert (no phantom gap) - and
            // `gapAfter` drops it for the truly-last pill so it never stacks a trailing
            // gap under the section's own `pb-1.5` (K3 fix).
            'for-you-row-motion flex items-start gap-2 rounded-[9px] bg-white/30 px-[11px] py-2',
            gapAfter && 'mb-2',
            rowPose,
          )}
          style={
            cascadeDelayMs === null
              ? undefined
              : ({ '--fy-row-delay': `${cascadeDelayMs}ms` } as CSSProperties)
          }
        >
          <button
            type="button"
            onClick={openPost}
            className={cn(
              'flex min-w-0 flex-1 items-start gap-2 rounded-xl text-left',
              // Wide body tints instead of scaling (the LiveCompactCard cue).
              'transition-colors duration-[160ms] ease-out active:bg-black/5',
              FOCUS_RING_CLASSES,
            )}
          >
            {/* 40px matches-card avatar: `MatchAvatarRing` (shared with HelperListItem,
                K4) owns the teal->periwinkle gradient ring; `object-cover` on the image
                keeps a non-square photo cropped, never stretched (the distorted-avatar
                guard). `shrink-0` moves onto the ring since this div, not the Avatar, is
                now the flex item; the row's `items-start` already keeps it unstretched on
                the cross axis, and flex's content-based main-axis sizing (no `justify-self`
                quirk here, unlike the grid-based HelperListItem) lets it grow to 43px
                (avatar + 1.5px each side) for free. */}
            <MatchAvatarRing className="shrink-0">
              <Avatar className="h-10 w-10">
                <AvatarImage
                  src={item.author.profile_picture ?? undefined}
                  alt=""
                  className="object-cover"
                />
                <AvatarFallback className="bg-[#e3e7ff] text-sm font-semibold text-mb-ink">
                  {item.author.full_name.trim().charAt(0).toUpperCase()}
                </AvatarFallback>
              </Avatar>
            </MatchAvatarRing>
            <span className="min-w-0 flex-1">
              <span className="block text-sm leading-snug text-gray-900">
                <span className="font-semibold">{item.author.full_name}</span> {item.title}
              </span>
              <span className="text-muted-foreground mt-0.5 block truncate text-xs">
                {item.reason}
              </span>
            </span>
          </button>
          <button
            type="button"
            aria-label="Dismiss"
            onClick={event => {
              event.stopPropagation();
              handleDismiss();
            }}
            className={cn(DISMISS_BUTTON_CLASSES, '-my-2 -mr-1.5')}
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * The overflow facepile shown in the collapsed disclosure toggle (mock `.fy-more`):
 * a tiny pile of the next overflow authors, decorative compression only - the
 * visible count text carries the number, so the pile is `aria-hidden`.
 */
function ForYouMoreFacepile({
  faces,
}: {
  /** Overflow authors carrying their question_id (the stable React key). */
  faces: { questionId: string; author: HelpableAtEventItem['author'] }[];
}) {
  if (faces.length === 0) {
    return null;
  }
  return (
    <span aria-hidden="true" className="flex shrink-0 items-center -space-x-1.5">
      {faces.map(({ questionId, author }) => (
        <ImgWithFallback
          key={questionId}
          src={author.profile_picture}
          alt=""
          className="h-5 w-5 shrink-0 rounded-full object-cover ring-2 ring-white"
          fallback={() => (
            // Initial disc grammar shared with GoingFacepile's fallback.
            <span className="bg-mb-teal-light text-mb-ink flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-xs font-semibold ring-2 ring-white">
              {author.full_name.trim().charAt(0).toUpperCase()}
            </span>
          )}
        />
      ))}
    </span>
  );
}

/**
 * The event match-gate card (fb6 r2, founder Figma node 6947-19514): the zero-state that must READ as
 * THE matching surface. A gradient-border frame (radius 10) over the Figma's offwhite #f0efeb, with
 * the settled matches card's OWN header - MatchCardMark + MATCH_CARD_TITLE, identical classes - kept
 * crisp above the body, so gate and matches states wear one identity.
 *
 * Two bodies, one frame (composition, not a boolean on the content):
 *   - PROFILE GATE (`teaseMatches`): the shipped, pre-blurred match rows (`/matches-gate-tease.png`)
 *     as a legible rows layer under a frost pane (white/80 + a single live backdrop-blur-4, radius 9),
 *     with the shared gate content stacked on top. The body floors at the Figma's 350:208 (a grid
 *     stack, so bg-cover keeps the rows reading as rows when content is short, and tall content grows
 *     the body rather than clipping); the frame padding + concentric r10/r9 make the white rows body
 *     read as an inset card against the offwhite frame.
 *   - POST INVITATION: the same frame + header with the content on the plain offwhite body - no tease,
 *     no frost (ruling 2: nothing waits behind an unwritten post).
 *
 * The content column is the SHARED NudgeCardContent (verbatim with the question-detail NudgeCard), so
 * the gate IS the same component the matches nudge is, not a lookalike. The Figma's top-right info
 * glyph is omitted (no defined behavior - no dead UI ships); the solid teal pill is kept over the
 * mock's pill-less minimalism as the explicit affordance (the >=40px hit-area rule + r1's tested CTA).
 */
function ForYouGateCard({
  icon,
  headline,
  body,
  ctaLabel,
  teaseMatches,
  onActivate,
}: {
  icon: ReactNode;
  headline: string;
  body: string;
  ctaLabel: string;
  /** Profile gate paints the tease rows + frost; the post invitation gets the plain framed body. */
  teaseMatches: boolean;
  onActivate: () => void;
}) {
  return (
    // Frame: the shared gradient border over the Figma offwhite #f0efeb (so the white rows body reads
    // as an inset card). gap-3.5 + py-3.5 px-2 are the Figma's 14 stack gap and 14/8 padding; the r10
    // frame / r9 body stays concentric per the Figma (inner = outer - 1, the color gap does the inset).
    <GradientBorderBox
      fill="#f0efeb"
      className="flex flex-col gap-3.5 rounded-[10px] px-2 py-3.5 shadow-soft"
    >
      {/* Header, reused verbatim from the settled matches card so the two share one identity - the
          same mark asset + title span, kept crisp (the frost only ever covers the body below it). */}
      <div className="flex items-center gap-1.5 px-1">
        <MatchCardMark />
        <span className="text-base font-semibold text-[#1c1c1c]">{MATCH_CARD_TITLE}</span>
      </div>
      {teaseMatches ? (
        // Profile gate body: the pre-blurred rows under the frost pane, gate content stacked on top.
        // A GRID STACK floors the body at the Figma's 350:208 without capping it - an invisible aspect
        // spacer and the content share ONE grid cell, so the row sizes to max(aspect, content): the
        // aspect is a FLOOR (short content keeps the rows' proportion via bg-cover), and taller content
        // on narrow widths grows the body instead of clipping. A plain flex + aspect-ratio could NOT
        // do this: the overflow-hidden that clips the tease + frost to r9 zeroes a flex item's
        // automatic minimum, turning aspect-ratio into a hard CEILING that cuts the icon top and pill
        // bottom below ~330px (verified in Chromium). py-3 + place-self keep the content off the clip
        // edge when it defines the height; the frost stays the one live backdrop-blur.
        <div className="relative grid grid-cols-1 overflow-hidden rounded-[9px]">
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 bg-[url(/matches-gate-tease.png)] bg-cover bg-center"
          />
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 bg-white/80 backdrop-blur-[4px]"
          />
          {/* Invisible aspect spacer: floors the shared grid row at the Figma 350:208 ratio. */}
          <div aria-hidden="true" className="col-start-1 row-start-1 aspect-[350/208] w-full" />
          <div className="relative z-10 col-start-1 row-start-1 flex w-full max-w-[302px] flex-col items-center justify-self-center gap-2.5 self-center py-3 text-center">
            <NudgeCardContent
              icon={icon}
              headline={headline}
              body={body}
              ctaLabel={ctaLabel}
              onActivate={onActivate}
            />
          </div>
        </div>
      ) : (
        // Post invitation body: the shared content on the plain offwhite frame - no rows, no frost.
        // Capped + centered to the Figma's ~302px content column, matching the gate variant.
        <div className="mx-auto flex w-full max-w-[302px] flex-col items-center gap-2.5 text-center">
          <NudgeCardContent
            icon={icon}
            headline={headline}
            body={body}
            ctaLabel={ctaLabel}
            onActivate={onActivate}
          />
        </div>
      )}
    </GradientBorderBox>
  );
}

/**
 * The zero-matches state (P8 - reverses the earlier no-render rule), now wearing the event match-gate
 * card (fb6 r2, ForYouGateCard - the founder's Figma). Exactly ONE card renders, gates before
 * advisories (ruling 1):
 *
 *   - profile incomplete (and a profile path can be built) -> the profile gate card WITH the tease:
 *     the shared `profile_empty` headline + CTA (single-sourced from the nudge constants) over the
 *     EVENT-contextual support line, the pre-blurred match rows under a frost pane behind it. Matches
 *     plausibly wait behind an empty profile, the matcher just cannot judge this viewer as a helper
 *     yet (ruling 2 / fb5 C1 honesty).
 *   - otherwise -> the post-invitation card (ruling 3), NO tease / NO frost: nothing waits behind a
 *     post that does not exist yet. A null / degraded viewer (no buildable profile path) also lands
 *     here rather than on a dead profile CTA (ruling 7).
 *
 * The gate card wears the settled matches card's header (mark + title) so it reads as the matching
 * surface, but keeps NONE of the matches state's live choreography - no wash, no cascade, no
 * border-light (those stay one level up, in ForYouSectionBody). The collapse shell still opens on
 * resolve, and the two impressions keep their timing: `for_you_section` fires on `open`
 * (FlaggedForYouSection); `helpable_invitation_shown` fires here once the card is actually shown
 * (`!boot`), for BOTH variants, via the StrictMode-safe ref.
 */
function ForYouZeroState({
  event,
  boot,
  eventContext,
  onOpenComposer,
}: {
  /** The event, threaded so the post CTA can tell whether a post from it has a host Space to land
   *  in (ruling 6). Optional: some test mounts pass only the id, and the post CTA then falls back
   *  to a bare create-post nav. */
  event?: EventRead;
  /** True while the section holds its pre-release boot pose (the impression gate). */
  boot: boolean;
  /** Prebuilt event + parent-space analytics context (W6). */
  eventContext: HelpableEventContext;
  /** The Event page's create-post entry (see ForYouSectionProps). */
  onOpenComposer?: (surface: EventComposerSurface) => void;
}) {
  const { user } = useCurrentUser();
  const { trackEvent } = useAmplitudeTracking();

  // The profile gate shows ONLY when the profile is incomplete AND a profile-edit path can be built;
  // a null / degraded viewer degrades to the post card instead of a dead CTA (ruling 7). The shared
  // useProfileEditCta hook owns the isPathBuildable gate + the returnTo navigation, single-sourced with
  // MatchStateSection's question-detail nudge so the profile-edit contract can't diverge (C5).
  const userEntity = { id: user?.id ?? null, slug: user?.slug ?? null };
  const { canEdit, openEdit } = useProfileEditCta(userEntity);
  const showProfileGate = isProfileIncomplete(user) && canEdit;

  // `helpable_invitation_shown` once per appearance, on the render the zero state is actually
  // SHOWN (spec 3.5). This component mounts ONLY at 0 matches, so its appearance IS the invitation
  // appearing (a last-card dismissal remounts it -> fires again), but a first-time motion-on viewer
  // holds a zero-height boot pose for a frame first - gating on `!boot` keeps it off the unseen boot
  // frame. The ref makes the StrictMode double-run fire exactly once. Fires for BOTH gate variants
  // (ruling 5); `helpable_count: 0` is intrinsic to the zero state.
  // The variant discriminator, mirroring helpable_invitation_cta_tapped's `cta` (same field + values),
  // so shown->tapped is a same-key join per variant and profile-gate impressions don't inflate the
  // post funnel (C2). Derived so it can enter the effect deps; the shownRef still fires the effect
  // exactly once, so if the variant ever flipped mid-mount the first-shown value wins (acceptable -
  // profile completeness / viewer identity don't flip within a single zero-state view).
  const shownCta = showProfileGate ? 'profile' : 'post';
  const shownRef = useRef(false);
  useEffect(() => {
    if (boot || shownRef.current) {
      return;
    }
    shownRef.current = true;
    trackEvent(ANALYTICS_EVENTS.helpable_invitation_shown, {
      cta: shownCta,
      helpable_count: 0,
      ...eventContext,
    });
  }, [boot, shownCta, trackEvent, eventContext]);

  const trackCta = (cta: 'post' | 'profile') => {
    trackEvent(ANALYTICS_EVENTS.helpable_invitation_cta_tapped, {
      cta,
      helpable_count: 0,
      ...eventContext,
    });
  };

  if (showProfileGate) {
    // The profile gate wears the event match-gate frame (fb6 r2): the settled card's header + mark,
    // the shipped tease rows under a frost pane, and the shared `profile_empty` headline + CTA
    // (single-sourced from the nudge constants, never re-typed) over the EVENT-contextual support
    // line - so the card reads as the door to THIS event's matching. The tease paints here (ruling 2):
    // matches plausibly wait behind an empty profile.
    return (
      <ForYouGateCard
        icon={profileNudgeIcon}
        headline={PROFILE_EMPTY_NUDGE_HEADLINE}
        body={FOR_YOU_PROFILE_GATE_COPY}
        ctaLabel={PROFILE_EMPTY_NUDGE_CTA}
        teaseMatches
        onActivate={() => {
          trackCta('profile');
          openEdit();
        }}
      />
    );
  }

  // Post-invitation card. The post CTA calls the Event page's create-post entry when one was
  // handed down and the event has a host Space (ruling 6: space pre-selection + returnTo + the
  // event_composer_opened signal, and the attend prompt first for someone not Going). The page owns
  // that entry and its prompt, not the card: this zero state unmounts as soon as the section has a
  // match to show, which a Going can cause. With no entry handed down the card is the bare one. So
  // is the card of an Event with no host Space, like the page's (+), which hides for it
  // (showEventPostEntry): a post from it has nowhere to land, so the attend prompt would run a
  // Going only for the composer to stop on "no space to post in".
  const onTrackPost = () => {
    trackCta('post');
  };
  return event && onOpenComposer && showEventPostEntry(event) ? (
    <SeededPostInvitationCard onOpenComposer={onOpenComposer} onTrackPost={onTrackPost} />
  ) : (
    <BarePostInvitationCard onTrackPost={onTrackPost} />
  );
}

/**
 * Post-invitation card WITH the event seeded: the post CTA calls the Event page's create-post entry
 * (`onOpenComposer`) as the `for_you_invitation` surface. The page's entry does the rest: for
 * someone Going it opens the composer for the event (host spaces ticked, returnTo set) and fires
 * `event_composer_opened { surface: 'for_you_invitation' }` (ruling 6); for someone not Going it
 * opens the page's attend prompt first. This card owns neither the prompt nor the Going it runs,
 * so both carry on when the card unmounts. The invitation's own
 * `helpable_invitation_cta_tapped { cta: 'post' }` fires alongside via `onTrackPost` - both funnels
 * stay honest.
 */
function SeededPostInvitationCard({
  onOpenComposer,
  onTrackPost,
}: {
  onOpenComposer: (surface: EventComposerSurface) => void;
  onTrackPost: () => void;
}) {
  return (
    <PostInvitationNudge
      onActivate={() => {
        onTrackPost();
        onOpenComposer('for_you_invitation');
      }}
    />
  );
}

/**
 * Post-invitation card with NO event to seed: no create-post entry handed down (the section mounted
 * off the Event page, some test mounts), no event threaded, or an Event with no host Space. The
 * post CTA falls back to today's bare create-post nav - no seeding, no attend prompt, no
 * event_composer_opened - while still firing the invitation's `cta: 'post'` funnel (ruling 6).
 */
function BarePostInvitationCard({ onTrackPost }: { onTrackPost: () => void }) {
  const navigate = useNavigate();
  return (
    <PostInvitationNudge
      onActivate={() => {
        onTrackPost();
        navigate(routes.question.create());
      }}
    />
  );
}

/**
 * The post-invitation gate card (ruling 3): the event match-gate frame + header with the app's
 * create-post glyph (lucide Pencil in the shared size-10.5 teal treatment), the "find the ones who
 * care" headline over the locked support line, and the "post what you need" pill. NO tease, NO frost
 * (ruling 2): nothing waits behind a post that does not exist yet, so the plain framed card carries
 * the header identity without the rows layer. Copy is single-sourced from the exported constants.
 */
function PostInvitationNudge({ onActivate }: { onActivate: () => void }) {
  return (
    <ForYouGateCard
      icon={<Pencil className={NUDGE_GLYPH_CLASS} aria-hidden="true" />}
      headline={FOR_YOU_INVITATION_HEADLINE}
      body={FOR_YOU_INVITATION_COPY}
      ctaLabel={FOR_YOU_INVITATION_CTA}
      teaseMatches={false}
      onActivate={onActivate}
    />
  );
}

/**
 * Section-scoped dismissal: the EXISTING helpable dismiss mutation
 * (`useDismissHelpable` - optimistic helper-feed surgery, 404-is-success,
 * rollback + toast, helper-feed invalidation) LAYERED with this surface's own
 * optimistic write on the `eventHelpableKey(eventId)` entry, so the card
 * leaves (and the last-card case becomes the invitation) without waiting for
 * the server. Per-call callbacks mirror the base hook's rules for THIS cache:
 * 404 keeps the removal, real failures re-insert THIS card (composing, so a
 * rapid second dismiss survives the first's rollback), and settle invalidates
 * via the factory key so the server reconciles the section (spec 3.2). W3 defers
 * the CALL until the card's exit transition lands (`useOwnedExit`), so the
 * scale-fade + grid heal play on a still-mounted row.
 */
function useDismissEventHelpable(eventId: string) {
  const queryClient = useQueryClient();
  const { mutate } = useDismissHelpable();

  return async (questionId: string) => {
    const key = eventHelpableKey(eventId);
    // Cancel in-flight reads so a landing response can't clobber the write.
    await queryClient.cancelQueries({ queryKey: key });
    const previous = queryClient.getQueryData<EventHelpableResponse>(key);
    queryClient.setQueryData<EventHelpableResponse>(key, old =>
      old ? removeHelpableItem(old, questionId) : old,
    );
    mutate(questionId, {
      onError: error => {
        // Base-hook parity: a 404 means the match was already retired
        // server-side - the removal IS the correct end state.
        if (isAxiosError(error) && error.response?.status === 404) {
          return;
        }
        // Re-insert ONLY this card, composing over any concurrent write.
        queryClient.setQueryData<EventHelpableResponse>(key, current =>
          restoreHelpableItem(current, previous, questionId),
        );
      },
      onSettled: () => {
        void queryClient.invalidateQueries({ queryKey: key });
      },
    });
  };
}
